import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './components/Controls.tsx';

/** Explicit choice of the toggle. Mirrored by the pre-paint script of the shell (apps/web/src/theme-bootstrap.js). */
export const themeCookie = 'lumlab-theme';
export type Theme = 'light' | 'dark';

const cookiePattern = new RegExp(`(?:^|;\\s*)${themeCookie}=(light|dark)(?:;|$)`);
const darkQuery = '(prefers-color-scheme: dark)';

export function storedTheme(cookie: string): Theme | null {
    const value = cookiePattern.exec(cookie)?.[1];
    return value === 'light' || value === 'dark' ? value : null;
}

function systemTheme(): Theme {
    return window.matchMedia(darkQuery).matches ? 'dark' : 'light';
}

function currentTheme(): Theme {
    return document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light';
}

/** Rewrites the choice for another year: Safari caps cookies written by scripts at 7 days, renewed on each visit. */
function rememberTheme(theme: Theme) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${themeCookie}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

function subscribe(onChange: () => void) {
    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
}

/**
 * Applied theme, a setter that remembers the choice for a year, and live system changes while none is stored. The
 * browser bar (`theme-color`) follows the applied theme rather than the system one.
 */
export function useTheme() {
    const theme = useSyncExternalStore(subscribe, currentTheme, (): Theme => 'light');
    useEffect(() => {
        const surface = getComputedStyle(document.documentElement).getPropertyValue('--lumlab-surface').trim();
        if (!surface) return;
        for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
            meta.content = surface;
        }
    }, [theme]);
    useEffect(() => {
        const stored = storedTheme(document.cookie);
        if (stored) rememberTheme(stored);
        const media = window.matchMedia(darkQuery);
        const follow = () => {
            if (!storedTheme(document.cookie)) document.documentElement.dataset['theme'] = systemTheme();
        };
        media.addEventListener('change', follow);
        return () => media.removeEventListener('change', follow);
    }, []);
    const setTheme = useCallback((next: Theme) => {
        rememberTheme(next);
        document.documentElement.dataset['theme'] = next;
    }, []);
    return { theme, setTheme };
}

/** One-click switch between the light and dark themes (moon in light mode, sun in dark mode). */
export function ThemeToggle() {
    const { t } = useTranslation();
    const { theme, setTheme } = useTheme();
    const label = theme === 'dark' ? t('platform.ui.theme.useLight') : t('platform.ui.theme.useDark');
    return (
        <Button
            variant="ghost"
            size="icon"
            aria-label={label}
            title={label}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
            <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="size-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                {theme === 'dark' ? (
                    <>
                        <circle cx="12" cy="12" r="4" />
                        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                    </>
                ) : (
                    <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
                )}
            </svg>
        </Button>
    );
}
